import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Palette, ExternalLink, Check, Hammer, Truck, Ban, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getAdminCoverRequests, decideCoverRequest, uploadAsset } from "@/lib/afmApi";
import { StatusBadge, COVER_REQUEST_STATUS } from "@/components/StatusBadge";
import { Loading, SectionTitle } from "@/components/common";
import { fmtDate } from "@/lib/format";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "staff"];
const FILTERS = [
  { key: "", label: "Todas" },
  { key: "pending_verification", label: "Pago en verificación" },
  { key: "verified", label: "Verificadas" },
  { key: "in_design", label: "En diseño" },
  { key: "delivered", label: "Entregadas" },
  { key: "rejected", label: "Rechazadas" },
];
const PAY_LABEL = { pago_movil: "Pago Móvil", paypal: "PayPal", binance: "Binance Pay" };

export default function AdminCoverRequests() {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role) || user?.super_admin;
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");
  const [acting, setActing] = useState(null); // { id, status }
  const [note, setNote] = useState("");
  const [deliveredUrl, setDeliveredUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    if (!isAdmin) { setStatus("denied"); return; }
    setStatus("loading");
    getAdminCoverRequests(user.id, filter)
      .then((d) => { setItems(Array.isArray(d) ? d : []); setStatus("ok"); })
      .catch((e) => setStatus(e?.response?.status === 403 ? "denied" : "error"));
  }, [user.id, filter, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, newStatus, extra = {}) => {
    setBusy(id);
    try {
      await decideCoverRequest(id, { viewer_id: user.id, status: newStatus, ...extra });
      toast.success("Solicitud actualizada");
      setActing(null); setNote(""); setDeliveredUrl("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); } finally { setBusy(""); }
  };

  const uploadDesign = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const { url } = await uploadAsset(user.id, file); setDeliveredUrl(url); toast.success("Diseño subido"); }
    catch { toast.error("No se pudo subir el archivo"); } finally { setUploading(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "denied") return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="cr-admin-denied">
      <div className="mb-4 rounded-full border border-amber-300 bg-amber-50 p-4 text-amber-700"><ShieldAlert className="h-7 w-7" /></div>
      <h3 className="font-serif text-xl font-bold text-black">Solo administradores</h3>
      <p className="mt-2 max-w-sm text-sm text-slate-500">Esta sección es exclusiva del equipo de Always Fresh.</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-cover-requests-page">
      <SectionTitle>Solicitudes de Portada</SectionTitle>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((ff) => (
          <button key={ff.key || "all"} data-testid={`cr-filter-${ff.key || "all"}`} onClick={() => setFilter(ff.key)} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === ff.key ? "border-black bg-black text-white" : "border-slate-300 bg-white text-slate-600 hover:border-black"}`}>{ff.label}</button>
        ))}
      </div>

      {!items.length ? (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-16 text-center text-sm text-slate-400">Sin solicitudes en esta categoría.</p>
      ) : (
        <div className="space-y-4">
          {items.map((r) => (
            <div key={r.id} data-testid={`cr-admin-item-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-serif text-lg font-bold text-black">{r.title}</h3>
                    <StatusBadge map={COVER_REQUEST_STATUS} status={r.status} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>{r.main_artist}{r.featuring ? ` ft. ${r.featuring}` : ""}</span>
                    <span>· {r.genre}</span>
                    <span>· {r.user_name || r.user_email || r.user_id}</span>
                    <span>· {fmtDate(r.created_at)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-lg font-bold text-[#AC3333]">{r.tier_label} · ${r.amount_usd}</div>
                  <div className="text-[11px] text-slate-500">{PAY_LABEL[r.payment_method] || r.payment_method} · Ref <span className="font-mono">{r.payment_reference}</span></div>
                </div>
              </div>

              {r.concept && <p className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{r.concept}</p>}

              <div className="mt-3 flex flex-wrap items-center gap-3">
                {r.payment_proof_url && (
                  <a href={r.payment_proof_url} target="_blank" rel="noreferrer" data-testid={`cr-proof-${r.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-[#AC3333] hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Ver comprobante</a>
                )}
                {(r.reference_urls || []).map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="h-12 w-12 overflow-hidden rounded-lg border border-slate-200"><img src={u} alt={`ref-${i}`} className="h-full w-full object-cover" /></a>
                ))}
              </div>

              {r.admin_note && <p className="mt-2 text-xs text-slate-500">Nota: {r.admin_note}</p>}
              {r.delivered_url && <a href={r.delivered_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:underline"><ExternalLink className="h-3.5 w-3.5" /> Arte entregado</a>}

              {/* Actions */}
              {acting?.id === r.id ? (
                <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`cr-acting-${r.id}`}>
                  {acting.status === "delivered" && (
                    <div className="space-y-2">
                      <label data-testid={`cr-upload-${r.id}`} className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-sm font-semibold text-slate-600 hover:border-black">
                        <Upload className="h-4 w-4" /> {uploading ? "Subiendo…" : "Subir archivo del diseño"}
                        <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadDesign(e.target.files?.[0])} />
                      </label>
                      {deliveredUrl && (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2" data-testid={`cr-delivered-preview-${r.id}`}>
                          <img src={deliveredUrl} alt="preview" className="h-12 w-12 shrink-0 rounded object-cover" />
                          <a href={deliveredUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-xs font-medium text-emerald-700 hover:underline">{deliveredUrl}</a>
                        </div>
                      )}
                      <input data-testid={`cr-delivered-url-${r.id}`} value={deliveredUrl} onChange={(e) => setDeliveredUrl(e.target.value)} placeholder="o pega una URL del arte final" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black" />
                    </div>
                  )}
                  <textarea data-testid={`cr-note-${r.id}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={acting.status === "rejected" ? "Motivo del rechazo (visible al artista)" : "Nota (opcional)"} className="w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-black" />
                  <div className="flex gap-2">
                    <button data-testid={`cr-confirm-${r.id}`} disabled={busy === r.id} onClick={() => decide(r.id, acting.status, { admin_note: note.trim(), delivered_url: deliveredUrl.trim() })} className="rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">Confirmar</button>
                    <button onClick={() => { setActing(null); setNote(""); setDeliveredUrl(""); }} className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium hover:border-black">Cancelar</button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex flex-wrap gap-2">
                  {r.status === "pending_verification" && <ActBtn tid={`cr-verify-${r.id}`} onClick={() => decide(r.id, "verified")} busy={busy === r.id} icon={Check} cls="border-blue-300 bg-blue-50 text-blue-700">Verificar pago</ActBtn>}
                  {["pending_verification", "verified"].includes(r.status) && <ActBtn tid={`cr-design-${r.id}`} onClick={() => decide(r.id, "in_design")} busy={busy === r.id} icon={Hammer} cls="border-violet-300 bg-violet-50 text-violet-700">En diseño</ActBtn>}
                  {["verified", "in_design"].includes(r.status) && <ActBtn tid={`cr-deliver-${r.id}`} onClick={() => { setActing({ id: r.id, status: "delivered" }); setNote(""); setDeliveredUrl(""); }} icon={Truck} cls="border-emerald-300 bg-emerald-50 text-emerald-700">Marcar entregado</ActBtn>}
                  {!["delivered", "rejected"].includes(r.status) && <ActBtn tid={`cr-reject-${r.id}`} onClick={() => { setActing({ id: r.id, status: "rejected" }); setNote(""); }} icon={Ban} cls="border-red-300 bg-red-50 text-red-700">Rechazar</ActBtn>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ActBtn = ({ tid, onClick, busy, icon: Icon, cls, children }) => (
  <button data-testid={tid} onClick={onClick} disabled={busy} className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${cls}`}>
    <Icon className="h-3.5 w-3.5" /> {children}
  </button>
);
